// src/components/ProtectedRoute/Admin/AdminContributionChart.jsx
"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Typography, Paper, FormControl, InputLabel, Select, MenuItem, Box, Stack, Divider
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Legend,
} from "recharts";

export default function AdminContributionChart({ stats, actorNameMap = new Map() }) {
  const { t, i18n } = useTranslation("adminTeam");
  const [selectedTable, setSelectedTable] = useState('all');

  const pickDisplayName = (disp) => {
    if (!disp) return null;
    if (typeof disp === "object") {
      const lang = (i18n.language || "").split("-")[0];
      return disp[lang] || disp.en || Object.values(disp)[0];
    }
    return disp;
  };

  const resolveActorName = (idOrName, fallback) => {
    if (!idOrName) return fallback || t("unknownUser", "unknown");
    if (actorNameMap.has(idOrName)) {
      return pickDisplayName(actorNameMap.get(idOrName)) || fallback || idOrName;
    }
    return fallback || idOrName;
  };

  // ドロップダウンとスタック用のテーブル名リスト
  const tableNames = useMemo(() => {
    return [...new Set(stats.map(s => (s.table_name || "").trim()).filter(Boolean))].sort();
  }, [stats]);

  // テーブルごとの色を生成
  const colors = useMemo(() => {
    const colorPalette = ['#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#ff8a65', '#8dd1e1', '#a4de6c', '#d0fb57', '#ff9f40'];
    const colorMap = {};
    tableNames.forEach((name, index) => {
      colorMap[name] = colorPalette[index % colorPalette.length];
    });
    return colorMap;
  }, [tableNames]);

  // propsで受け取った集計済みデータにパーセンテージとラベルを追加
  const data = useMemo(() => {
    // ユーザーごとにテーブルの貢献度をまとめる（ピボット）
    const userContributions = new Map();
    stats.forEach((row) => {
      const { username, table_name, contribution_count, actor_id, user_id, auth_user_id, count } = row;
      const key = auth_user_id || actor_id || user_id || username;
      const tname = (table_name || "").trim();
      if (!key || !tname) return;
      const cntRaw = contribution_count ?? count ?? 0;
      const cnt = Number.isFinite(cntRaw) ? Number(cntRaw) : parseInt(cntRaw, 10) || 0;
      if (cnt <= 0) return;
      if (!userContributions.has(key)) {
        const label = resolveActorName(key, username);
        userContributions.set(key, { userId: key, user: label, total: 0 });
      }
      const userStat = userContributions.get(key);
      userStat[tname] = (userStat[tname] || 0) + cnt;
      userStat.total += cnt;
    });

    let processedData = Array.from(userContributions.values());
    console.info("[AdminContribution] processedData", processedData);

    // 特定のテーブルが選択された場合、そのテーブルのデータのみにフィルタリング
    if (selectedTable !== 'all') {
      return processedData
        .filter(d => d[selectedTable])
        .map(d => ({
          userId: d.userId,
          user: d.user || resolveActorName(d.userId),
          count: d[selectedTable],
          label: d[selectedTable].toLocaleString(),
        }))
        .sort((a, b) => b.count - a.count);
    }

    // 全テーブル表示の場合、合計貢献度でソート
    processedData.sort((a, b) => b.total - a.total);
    return processedData;

  }, [stats, selectedTable, actorNameMap, i18n.language]);
  
  useEffect(() => {
    console.info("[AdminContribution] chart display data", data);
  }, [data]);
  
  // デバッグログ
  useEffect(() => {
    console.info("[AdminContribution] chart input stats:", stats);
    console.info("[AdminContribution] chart actorNameMap keys:", Array.from(actorNameMap.keys()));
    console.info("[AdminContribution] chart tableNames:", tableNames);
  }, [stats, actorNameMap, tableNames]);

  const handleTableChange = (event) => {
    setSelectedTable(event.target.value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, bgcolor: 'rgba(30, 30, 30, 0.9)', border: '1px solid #555' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {payload.slice().reverse().map(p => ( // reverse to show top of stack first
              <Typography key={p.dataKey} variant="body2" sx={{ color: p.color }}>
                {t(`tables.${p.dataKey}`, { defaultValue: p.dataKey })}: {p.value.toLocaleString()}
              </Typography>
            ))}
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {t('total', 'Total')}: {data.total.toLocaleString()}
            </Typography>
          </Stack>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 2, my: 4, backgroundColor: "#1e1e1e" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>

        <FormControl size="small" sx={{ m: 1, minWidth: 180 }}>
          <InputLabel id="table-select-label" sx={{ color: '#ccc' }}>{t('filterByTable', 'Filter by table')}</InputLabel>
          <Select
            labelId="table-select-label"
            value={selectedTable}
            label={t('filterByTable', 'Filter by table')}
            onChange={handleTableChange}
            sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#555' }, '.MuiSvgIcon-root': { color: '#fff' } }}
          >
            <MenuItem value="all">{t('allTables', 'All Tables')}</MenuItem>
            {tableNames.map(name => (
              <MenuItem key={name} value={name}>{t(`tables.${name}`, { defaultValue: name })}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 20, bottom: 20, left: 120 }}
          barCategoryGap={8}
        >
          <CartesianGrid stroke="#444" vertical={false} strokeDasharray="3 3" />

          <XAxis
            type="number"
            tick={{ fill: "#ddd", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            domain={[0, "dataMax"]}
          />

          <YAxis
            dataKey="user"
            type="category"
            width={140}
            tick={{ fill: "#ddd", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />

          {selectedTable === 'all' ? (
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.1)" }} />
          ) : (
            <Tooltip
              contentStyle={{ backgroundColor: "#222", border: "1px solid #555", borderRadius: 4, color: "#fff" }}
              cursor={{ fill: "rgba(255,255,255,0.1)" }}
              formatter={(value) => [value.toLocaleString(), t(`tables.${selectedTable}`, { defaultValue: selectedTable })]}
            />
          )}

          <Legend formatter={(value) => t(`tables.${value}`, { defaultValue: value })} />

          {selectedTable === 'all' ? (
            tableNames.map(tableName => (
              <Bar key={tableName} dataKey={tableName} stackId="a" name={tableName} fill={colors[tableName]} />
            ))
          ) : (
            <Bar dataKey="count" name={selectedTable} fill="#82ca9d" barSize={24}>
              <LabelList dataKey="label" position="right" style={{ fill: "#fff", fontSize: 12 }} />
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
